import { IconNames } from '@dixa/lib-shared-habla';
import { ReactNode } from 'react';

import { KnowledgeAiDraftAuditLogEntry } from 'apis/auditLogs';

/** knowledge-backend's stand-in author for changes the AI made on its own. */
export const anonymousUserId = 'aaaaaaaa-bbbb-4bbb-bbbb-41eb0c1b6fc9';

/** Whether the AI, rather than an agent, made the change. */
export const isByAi = (entry: KnowledgeAiDraftAuditLogEntry): boolean =>
  entry.author.id === anonymousUserId;

type Description = {
  readonly icon: IconNames;
  /** The sentence, given whoever made the change as a name or link. */
  readonly text: (author: ReactNode) => ReactNode;
};

const aiIcon: IconNames = 'ai';
const agentIcon: IconNames = 'user';

/**
 * Says what an entry did, in a sentence. Change types and reasons are plain
 * strings that knowledge-backend may extend, so anything unknown still reads
 * as an update rather than failing.
 */
export function describeAiDraftEntry(
  entry: KnowledgeAiDraftAuditLogEntry,
): Description {
  const icon = isByAi(entry) ? aiIcon : agentIcon;

  if (entry.activity_type === 'AI_DRAFT_DELETED') {
    switch (entry.reason) {
      case 'manualByUser':
        return { icon, text: (author) => <>{author} discarded it</> };
      case 'stale':
        return {
          icon: aiIcon,
          text: () => <>AI removed it as no longer relevant</>,
        };
      case 'approved':
        return {
          icon: aiIcon,
          text: () => <>Removed, as an article now covers it</>,
        };
      default:
        return { icon, text: (author) => <>{author} deleted it</> };
    }
  }

  switch (entry.change_type) {
    case 'create':
      return entry.reason === 'judge'
        ? { icon: aiIcon, text: () => <>AI suggested this article</> }
        : { icon, text: (author) => <>Drafted by {author}</> };
    case 'updateContent':
      switch (entry.reason) {
        case 'judge':
          return { icon: aiIcon, text: () => <>AI revised the draft</> };
        case 'selfHealingRebase':
          return {
            icon: aiIcon,
            text: () => (
              <>AI updated the draft after its article was published</>
            ),
          };
        case 'selfHealingStale':
          return {
            icon: aiIcon,
            text: () => (
              <>AI updated the draft after a related article was published</>
            ),
          };
        default:
          return {
            icon,
            text: (author) => <>{author} asked AI to revise it</>,
          };
      }
    case 'updateFiling':
      return {
        icon,
        text: (author) => <>{author} changed where it is filed</>,
      };
    case 'fullUpdate':
      return {
        icon,
        text: (author) => (
          <>{author} edited the content and changed where it is filed</>
        ),
      };
    case 'approve':
      return { icon, text: (author) => <>{author} added it to drafts</> };
    case 'reject':
      return { icon, text: (author) => <>{author} discarded it</> };
    default:
      return { icon, text: (author) => <>{author} updated it</> };
  }
}
